/**
 * Audit metadata parser (patch E6).
 *
 * The server stores audit metadata as a JSON-encoded string on
 * `metadataJson`. The UI needs a small structured view to render:
 *
 *   - A cascade reason ("Cascade from parent part deletion")
 *   - A rejection comment (italic quoted)
 *   - A generic "<key>: <value>" listing for ad-hoc contexts
 *
 * This helper does the JSON parse + shape detection in one place so
 * every audit-display surface (global audit log, per-project tab,
 * future drill-ins) renders cascade context the same way.
 *
 * Returns null when there's nothing to show (empty/null metadata).
 */

import { formatCurrency } from '@procurement/shared';

export type AuditMetadataView =
  | { kind: 'reason'; label: string }
  | { kind: 'rejection'; comment: string }
  | { kind: 'generic'; entries: ReadonlyArray<{ key: string; value: string }> };

/** Well-known reason codes that get a friendly label. */
const KNOWN_REASONS: Record<string, string> = {
  parent_part_deleted: 'Cascade from parent part deletion',
  password_reset: 'Admin password reset',
};

const FRIENDLY_KEYS: Record<string, string> = {
  origin: 'Origin',
  indentNumber: 'Indent number',
  indentDate: 'Indent date',
  makerNameBrand: 'Maker / brand',
  makeBrand: 'Make/brand',
  itemNumber: 'Part / sub-part #',
  name: 'Noun',
  uom: 'UOM',
  remarks: 'Remarks',
  contactName: 'Contact person / office name',
  address: 'Address',
  designation: 'Designation',
  officialPhone: 'Phone',
  personalPhone: 'Personal phone',
  email: 'Email',
  fax: 'Fax',
  isPrimary: 'Primary contact',
  totalStoreValuePaisa: 'Total store value',
  gstRateBps: 'GST %',
  requiredBgAmountPaisa: 'BG amount',
  bgSubmissionDurationDays: 'BG submission duration days',
  bgValidityDurationMonths: 'BG validity duration months',
  termsOfDelivery: 'Terms of delivery',
  paymentOnINoteBps: 'Payment on I-Note %',
  paymentOnCrvBps: 'Payment on CRV %',
};

function formatInspectionLocationValue(value: string): string {
  if (value === 'OUR_PREMISES') return 'Firm’s premises';
  if (value === 'CLIENT_PREMISES') return 'Client premises';
  if (value === 'THIRD_PARTY_PREMISES') return 'Third party premises';
  return value;
}

function formatGenericAuditValue(key: string, value: string | number | boolean): string {
  if (typeof value === 'number') {
    if (key.endsWith('Paisa')) return formatCurrency(value);
    if (key.endsWith('Bps')) return `${(value / 100).toFixed(2)}%`;
  }
  if (typeof value === 'string') return formatInspectionLocationValue(value);
  return String(value);
}


export function parseAuditMetadata(metadataJson: string | null): AuditMetadataView | null {
  if (metadataJson === null || metadataJson === '') return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(metadataJson);
  } catch {
    // Defensive — if the server ever emits non-JSON metadata (shouldn't),
    // surface it verbatim rather than throwing.
    return { kind: 'generic', entries: [{ key: 'raw', value: metadataJson }] };
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }

  const obj = parsed as Record<string, unknown>;

  // 1. Recognized reason code wins (most common cascade case).
  if (typeof obj.reason === 'string') {
    const friendly = KNOWN_REASONS[obj.reason] ?? obj.reason;
    return { kind: 'reason', label: friendly };
  }

  // 2. Rejection comment from parts-approval REJECT.
  if (typeof obj.rejectionComment === 'string' && obj.rejectionComment !== '') {
    return { kind: 'rejection', comment: obj.rejectionComment };
  }

  // 3. Generic — flatten primitive fields as <key>: <value>.
  const entries: { key: string; value: string }[] = [];
  for (const [key, val] of Object.entries(obj)) {
    if (val === null || val === undefined) continue;
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
      entries.push({ key: FRIENDLY_KEYS[key] ?? key, value: formatGenericAuditValue(key, val) });
    } else {
      entries.push({ key, value: JSON.stringify(val) });
    }
  }
  if (entries.length === 0) return null;
  return { kind: 'generic', entries };
}
